import React, { useState } from 'react';
import Container from '@mui/material/Container';
import { IconButton, InputBase, Paper } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { Typewriter } from 'react-simple-typewriter';
import DemoResponse from './DemoResponse';
import DemoButtons from "./DemoButtons";

const DemoComponent = () => {
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(true);
    const [response, setResponse] = useState(null);
    const [requestSent, setRequestSent] = useState(false);

    // Handle input changes
    const handleInputChange = (event) => {
        setInputValue(event.target.value);
        if (event.target.value.trim() !== '') {
            setIsTyping(false);
        }
    };

    // Handle Close Icon
    const closeDemoResponse = () => {
        setRequestSent(false);
        setResponse(null);
    }

    // Handle sending the input to the backend
    const handleSend = async () => {
        console.log(inputValue);
        if (inputValue.trim() !== '') {
            setInputValue('');
            setIsTyping(true);
            setRequestSent(true);
            setResponse(null);

            try {
                // Make a POST request to your backend
                const response = await fetch(process.env.REACT_APP_BASE_URL + '/data', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email_text: inputValue }),
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch summary from the backend');
                }

                const data = await response.json();

                // Filter function to clean up the response data
                const filterValue = (value) => {
                    if (value === null || value === undefined) return 'N/A';
                    if (typeof value === 'string' && value.trim() === '') return 'N/A';
                    if (typeof value === 'number' && value === 0) return 'N/A';
                    if (Array.isArray(value) && value.length === 0) return 'N/A';
                    if (value === '#') return 'N/A';
                    return value;
                };

                // Filter the features object
                const filteredFeatures = Object.fromEntries(
                    Object.entries(data.features).map(([key, value]) => [
                        key,
                        Array.isArray(value)
                            ? value.filter(v => v !== 0 && v !== '').join(', ')
                            : filterValue(value),
                    ])
                );

                setResponse((prev) => ({
                    ...prev,
                    id: Date.now(),
                    summary: filterValue(data.summary) !== 'N/A' ? filterValue(data.summary) : 'No summary available',
                    done: false,
                    originalEmail: inputValue,
                    ...filteredFeatures,
                }));

            } catch (error) {
                console.error('Error:', error);
                setResponse({
                    id: Date.now(),
                    summary: 'Error: Unable to generate summary',
                    done: false,
                    originalEmail: inputValue,
                    features: [],
                });
            }
        }
    };

    // Handle pressing Enter to send the input
    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleSend();
        }
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                padding: { xs: 2, sm: 3 }, // Adjust padding for mobile
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '4px 8px',
                    borderRadius: '50px',
                    marginTop: { xs: 10, sm: 20 }, // Adjust margin for mobile
                    width: '100%', // Full width on mobile
                }}
            >
                {isTyping && (
                    <span
                        style={{
                            color: 'gray',
                            position: 'absolute',
                            left: { xs: '8px', sm: '16px' }, // Adjust position for mobile
                            pointerEvents: 'none',
                            whiteSpace: 'nowrap',
                            fontSize: { xs: '12px', sm: '14px' }, // Adjust font size for mobile
                        }}
                    >
                        <Typewriter words={['Paste or enter the email here...']} loop={1} typeSpeed={45} />
                    </span>
                )}
                <InputBase
                    sx={{
                        flex: 1,
                        paddingLeft: 1,
                        zIndex: 1,
                        fontSize: { xs: '14px', sm: '16px' }, // Adjust font size for mobile
                        whiteSpace: 'pre-wrap', // Ensures line breaks are preserved
                        overflowWrap: 'break-word', // Allows word wrapping
                    }}
                    placeholder={!isTyping ? 'Paste or enter the email here...' : ''}
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    multiline={true} // Enable multi-line input
                    minRows={3} // Minimum number of rows
                    maxRows={10} // Maximum number of rows
                />

                <IconButton color="primary" onClick={handleSend}>
                    <SendIcon />
                </IconButton>
            </Paper>
            {requestSent && <DemoResponse response={response} closeDemoResponse={closeDemoResponse} />}
            {!requestSent && <DemoButtons setInputValue={setInputValue} setIsTyping={setIsTyping} />}
        </Container>
    );
};

export default DemoComponent;